import React, { useState, useEffect } from "react";
import authApiService from "../../services/authServices";
import { jwtDecode } from "jwt-decode";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ThreeDots } from "react-loader-spinner";
import {
  didWinSet,
  resolveTiebreakSuperscript,
} from "../../utils/scorecardDisplay";
import { formatSeasonDisplayName } from "../../utils/seasonName";

const ViewResult = () => {
  const [viewResult, setViewResult] = useState([]);
  const [userId, setUserId] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const matchId = searchParams.get("matchId");
  const opponentIdParam = searchParams.get("userId");
  const opponentId =
    opponentIdParam === "null" || opponentIdParam === ""
      ? null
      : opponentIdParam;
  const scheduleUserId = searchParams.get("scheduleUserId"); // Original userId from schedule page
  const seasonId = searchParams.get("seasonId");
  const previewPayload = location.state?.previewPayload || null;
  const previewMeta = location.state?.previewMeta || {};
  const isPreviewMode = Boolean(previewPayload);

  useEffect(() => {
    decodeToken();
  }, []);

  useEffect(() => {
    // Call API if matchId is available
    const effectiveUserId = opponentId || scheduleUserId || userId;
    if (matchId && !isPreviewMode) {
      fetchViewResult();
    }
  }, [userId, matchId, opponentId, scheduleUserId, isPreviewMode]);

  const decodeToken = () => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserId(decoded.userId);
        setUserRole(decoded.role || null);
      } catch (error) {
        console.error("Error decoding token:", error);
      }
    }
  };

  const fetchViewResult = async () => {
    try {
      setIsLoading(true);
      const effectiveUserId = opponentId || scheduleUserId || userId || "";
      const response = await authApiService.get(
        `viewResults?matchId=${matchId}&userId=${effectiveUserId}`,
      );
      if (response && response.data) {
        setViewResult(response.data);
      }
    } catch (error) {
      console.error("Error fetching schedule data:", error);
      setViewResult([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getIsPlayer1Winner = (result) => {
    if (!result) return true;
    if (
      result?.matchStatus === "BYE" ||
      (result.golfer2 == null && result.golfer1)
    ) {
      return true;
    }
    if (result.default_winner) {
      return String(result.default_winner) === String(result.golfer1);
    }
    if (result.opponentRetired) {
      return String(result.opponentRetired) !== String(result.golfer1);
    }
    if (result.puttoff_winner) {
      return String(result.puttoff_winner) === String(result.golfer1);
    }

    const p1Sets = [result.p1set1, result.p1set2, result.p1set3];
    const p2Sets = [result.p2set1, result.p2set2, result.p2set3];
    let p1Wins = 0;
    let p2Wins = 0;
    for (let i = 0; i < 3; i++) {
      if (p1Sets[i] > p2Sets[i]) p1Wins++;
      else if (p2Sets[i] > p1Sets[i]) p2Wins++;
    }
    return p1Wins > p2Wins;
  };

  const calculateSetsWon = (result, playerOneName, playerTwoName) => {
    if (!result) return { winner: "N/A", score: "0 - 0" };
    const isPlayer1Winner = getIsPlayer1Winner(result);
    const winner = isPlayer1Winner ? playerOneName : playerTwoName;

    if (result.default_winner) {
      return { winner, score: "2 - 0", wonByDefault: true };
    }

    const p1Sets = [result.p1set1, result.p1set2, result.p1set3];
    const p2Sets = [result.p2set1, result.p2set2, result.p2set3];
    let p1Wins = 0;
    let p2Wins = 0;
    for (let i = 0; i < 3; i++) {
      if (p1Sets[i] > p2Sets[i]) p1Wins++;
      else if (p2Sets[i] > p1Sets[i]) p2Wins++;
    }
    const setsWon = isPlayer1Winner ? p1Wins : p2Wins;
    const setsLost = isPlayer1Winner ? p2Wins : p1Wins;

    return { winner, score: `${setsWon} - ${setsLost}`, wonByDefault: false };
  };

  const previewResult = isPreviewMode
    ? {
        matchStatus:
          previewPayload?.bDefault === "true"
            ? "Default Win"
            : previewPayload?.opponentRetired === "true"
              ? "Opponent Retired"
              : "Scheduled",
        default_winner:
          previewPayload?.bDefault === "true"
            ? previewPayload?.default_winner ||
              previewMeta?.enteringUserId ||
              previewMeta?.player1Id ||
              null
            : null,
        // opponentRetired field stores the user id of the player who retired
        opponentRetired:
          previewPayload?.opponentRetired === "true"
            ? previewMeta?.opponentId ||
              // fallback: other player vs enterer
              (previewMeta?.enteringUserId != null &&
              String(previewMeta.enteringUserId) === String(previewMeta?.player1Id)
                ? previewMeta?.player2Id
                : previewMeta?.player1Id) ||
              null
            : null,
        puttoff_winner: previewPayload?.puttoff_winner || null,
        golfer1: previewMeta?.player1Id || null,
        golfer2: previewMeta?.player2Id || null,
        season_name:
          previewMeta?.seasonName || viewResult[0]?.season_name || "Season",
        play_by:
          previewPayload?.playedDate || previewMeta?.playBy || new Date(),
        p1set1: Number(previewPayload?.scores?.p1set1 || 0),
        p1set2: Number(previewPayload?.scores?.p1set2 || 0),
        p1set3: Number(previewPayload?.scores?.p1set3 || 0),
        p2set1: Number(previewPayload?.scores?.p2set1 || 0),
        p2set2: Number(previewPayload?.scores?.p2set2 || 0),
        p2set3: Number(previewPayload?.scores?.p2set3 || 0),
        p1set1sup: Number(previewPayload?.scores?.p1set1sup || 0),
        p1set2sup: Number(previewPayload?.scores?.p1set2sup || 0),
        p1set3sup: Number(previewPayload?.scores?.p1set3sup || 0),
        p2set1sup: Number(previewPayload?.scores?.p2set1sup || 0),
        p2set2sup: Number(previewPayload?.scores?.p2set2sup || 0),
        p2set3sup: Number(previewPayload?.scores?.p2set3sup || 0),
      }
    : null;

  const resultRows = isPreviewMode ? [previewResult] : viewResult;

  const formatDateOrDash = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  };

  let playerOneName =
    previewMeta?.player1Name ||
    (resultRows[0]
      ? `${resultRows[0]?.first_name || ""} ${resultRows[0]?.last_name || ""}`.trim()
      : "");
  let playerTwoName =
    previewMeta?.player2Name ||
    (resultRows[1]
      ? `${resultRows[1]?.first_name || ""} ${resultRows[1]?.last_name || ""}`.trim()
      : "");

  // Fallback: if playerTwoName is empty, try to get from viewResult[0] opponent fields
  if (!playerTwoName && resultRows[0]) {
    playerTwoName =
      `${resultRows[0]?.opponent_first_name || ""} ${resultRows[0]?.opponent_last_name || ""}`.trim();
  }

  if (resultRows.length > 0) {
    const { winner, score } = calculateSetsWon(
      resultRows[0],
      playerOneName,
      playerTwoName,
    );
    console.log(`Winner: ${winner}, Won: ${score}`);
  }

  const handleFinalPost = async () => {
    if (!isPreviewMode) return;
    try {
      const response = await authApiService.post(
        `postResults?matchId=${matchId}&userId=${opponentId || scheduleUserId || userId || ""}`,
        previewPayload,
      );
      const isSuccess =
        response?.code === 200 ||
        response?.status === 200 ||
        response?.success === true;

      if (isSuccess) {
        navigate(
          `/carter/schedule/viewresult?matchId=${matchId}&userId=${opponentId || scheduleUserId || userId || ""}&scheduleUserId=${scheduleUserId || userId || ""}`,
        );
      }
    } catch (error) {
      console.error("Error posting final result from preview:", error);
    }
  };

  return (
    <div className="page-container record-view-page view-result-page col-sm-9">
      {isLoading && !isPreviewMode ? (
        <div
          className="loader-container"
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <ThreeDots
            height="80"
            width="80"
            radius="9"
            color="#4fa94d"
            ariaLabel="three-dots-loading"
            visible={true}
          />
        </div>
      ) : (
        <>
          <div className="section">
            <div className="left userInfo">
              <h2>Match Results</h2>
              <p className="user"></p>
              {resultRows.length > 0 &&
                (() => {
                  const result = resultRows[0];
                  if (result?.matchStatus === "BYE") {
                    return null;
                  }
                  const { winner, score, wonByDefault } = calculateSetsWon(
                    result,
                    playerOneName,
                    playerTwoName,
                  );
                  return (
                    <p className="division" style={{ color: "#fff" }}>
                      {wonByDefault
                        ? `${winner || "N/A"} won by Default`
                        : `${winner || "N/A"} won ${score}`}
                    </p>
                  );
                })()}
            </div>
            <div className="clear" />
          </div>
          <div className="section">
            <div className="left">
              <div className="">
                <div className="component-page">
                  <div className="row mt-5 ">
                    <div className="hd-title col-lg-6">
                      <div className=" position-relative mt-40">
                        {(() => {
                          const splitSeasonName = (fullName) => {
                            if (!fullName) return { top: "", side: "" };
                            const keywords = ["Singles", "Doubles", "Mixed"];
                            let idx = -1;
                            for (const key of keywords) {
                              const i = fullName.indexOf(key);
                              if (i !== -1) {
                                idx = i;
                                break;
                              }
                            }
                            if (idx === -1) return { top: fullName, side: "" };
                            return {
                              top: fullName.slice(0, idx).trim(),
                              side: fullName.slice(idx).trim(),
                            };
                          };
                          const { top: seasonTop, side: seasonSide } =
                            splitSeasonName(
                              formatSeasonDisplayName(resultRows[0]?.season_name)
                            );
                          const seasonFullName = [seasonTop, seasonSide]
                            .filter(Boolean)
                            .join(" ");
                          return (
                            <>
                              <div className="selected-country">
                                <p
                                  style={{
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                  title={seasonSide || resultRows[0]?.season_name || ""}
                                >
                                  {seasonSide || resultRows[0]?.season_name}
                                </p>
                              </div>
                              <div className="matchDate" style={{ width: "100%" }}>
                                <div
                                  className="match-date"
                                  style={{
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                  title={`${formatDateOrDash(
                                    resultRows[0]?.play_by || resultRows[0]?.played,
                                  )} ${seasonFullName ? seasonFullName : ""}`.trim()}
                                >
                                  {formatDateOrDash(
                                    resultRows[0]?.play_by ||
                                      resultRows[0]?.played,
                                  )}{" "}
                                  {seasonFullName ? seasonFullName : ""}
                                </div>
                              </div>
                            </>
                          );
                        })()}
                        {resultRows.length > 0 &&
                          (() => {
                            const result = resultRows[0];

                            const isPlayer1Winner = getIsPlayer1Winner(result);

                            // Determine if current user (scheduleUserId or opponentId) is winner
                            const currentUserId =
                              scheduleUserId || opponentId || userId;
                            // For BYE, compare with golfer1 (only player)
                            const winnerId = isPlayer1Winner
                              ? result.golfer1
                              : result.golfer2;
                            const isCurrentUserWinner =
                              currentUserId &&
                              String(winnerId) === String(currentUserId);

                            return (
                              <div className="match-status">
                                {isCurrentUserWinner ? "W" : "L"}
                              </div>
                            );
                          })()}
                        {resultRows.length === 0 && (
                          <div className="match-status">W</div>
                        )}
                        <div className="score-list">
                          {resultRows.length > 0 &&
                            (() => {
                              const result = resultRows[0];

                              // BYE: keep same scorecard layout as normal results
                              if (
                                result?.matchStatus === "BYE" ||
                                (result.golfer2 == null && result.golfer1)
                              ) {
                                const winnerName =
                                  playerOneName ||
                                  `${result?.first_name || ""} ${result?.last_name || ""}`.trim() ||
                                  "Player";
                                const winnerIsHome =
                                  String(result.golfer1) === String(userId);
                                const winnerIconSrc = "/images/icon-12.png";
                                return (
                                  <table className="centerAlign linescore">
                                    <tbody>
                                      {/* Winner Row */}
                                      <tr>
                                        <td className="leftAlign strong">
                                          <div className="infodiv_sec">
                                            <div>
                                              <img
                                                className="align-middle"
                                                style={{
                                                  width: 40,
                                                  borderRadius: "50%",
                                                  marginRight: 8,
                                                }}
                                                src={winnerIconSrc}
                                                alt="Winner"
                                              />
                                            </div>
                                            <div className="d-block w-100 g-textCol">
                                              {winnerName} <br />
                                              <b
                                                className={
                                                  winnerIsHome
                                                    ? "homeTextVAl d-block pt-2"
                                                    : "awayTextVAl d-block pt-2"
                                                }
                                              >
                                                {winnerIsHome ? "Home" : "Away"}
                                              </b>
                                            </div>
                                          </div>
                                        </td>

                                        {/* MATCH NORMAL STRUCTURE */}
                                        <td className="strong runs">
                                          <span className="position-relative">
                                            6
                                          </span>
                                        </td>
                                        <td className="strong runs">
                                          <span className="position-relative">
                                            6
                                          </span>
                                        </td>
                                      </tr>

                                      {/* Loser Row */}
                                      <tr>
                                        <td className="leftAlign strong">
                                          <div className="infodiv_sec">
                                            <div
                                              className="d-block"
                                              style={{ color: "#666" }}
                                            ></div>
                                          </div>
                                        </td>

                                        <td className="runs">
                                          <span className="position-relative">
                                            0
                                          </span>
                                        </td>
                                        <td className="runs">
                                          <span className="position-relative">
                                            0
                                          </span>
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                );
                              }

                              const isPlayer1Winner =
                                getIsPlayer1Winner(result);

                              // Determine winner and loser data
                              const winnerName = isPlayer1Winner
                                ? playerOneName
                                : playerTwoName;
                              const loserName = isPlayer1Winner
                                ? playerTwoName
                                : playerOneName;
                              const winnerIcon = isPlayer1Winner
                                ? "/images/icon-12.png"
                                : "/images/icon-13.png";
                              const loserIcon = isPlayer1Winner
                                ? "/images/icon-13.png"
                                : "/images/icon-12.png";
                              const winnerIsHome = isPlayer1Winner
                                ? result.golfer1 === userId
                                : result.golfer2 === userId;

                              // Winner scores
                              const winnerSet1 = result.default_winner
                                ? 6
                                : isPlayer1Winner
                                  ? result.p1set1
                                  : result.p2set1;
                              const winnerSet2 = result.default_winner
                                ? 6
                                : isPlayer1Winner
                                  ? result.p1set2
                                  : result.p2set2;
                              const winnerSet3 = result.default_winner
                                ? 0
                                : isPlayer1Winner
                                  ? result.p1set3
                                  : result.p2set3;
                              const winnerSet1Sup = isPlayer1Winner
                                ? result.p1set1sup
                                : result.p2set1sup;
                              const winnerSet2Sup = isPlayer1Winner
                                ? result.p1set2sup
                                : result.p2set2sup;
                              const winnerSet3Sup = isPlayer1Winner
                                ? result.p1set3sup
                                : result.p2set3sup;

                              // Loser scores
                              const loserSet1 = result.default_winner
                                ? 0
                                : isPlayer1Winner
                                  ? result.p2set1
                                  : result.p1set1;
                              const loserSet2 = result.default_winner
                                ? 0
                                : isPlayer1Winner
                                  ? result.p2set2
                                  : result.p1set2;
                              const loserSet3 = result.default_winner
                                ? 0
                                : isPlayer1Winner
                                  ? result.p2set3
                                  : result.p1set3;
                              const loserSet1Sup = isPlayer1Winner
                                ? result.p2set1sup
                                : result.p1set1sup;
                              const loserSet2Sup = isPlayer1Winner
                                ? result.p2set2sup
                                : result.p1set2sup;
                              const loserSet3Sup = isPlayer1Winner
                                ? result.p2set3sup
                                : result.p1set3sup;
                              const showThirdSet =
                                Number(winnerSet3 || 0) > 0 ||
                                Number(loserSet3 || 0) > 0;

                              const tb1 = resolveTiebreakSuperscript(
                                winnerSet1,
                                loserSet1,
                                winnerSet1Sup,
                                loserSet1Sup,
                              );
                              const tb2 = resolveTiebreakSuperscript(
                                winnerSet2,
                                loserSet2,
                                winnerSet2Sup,
                                loserSet2Sup,
                              );
                              const tb3 = resolveTiebreakSuperscript(
                                winnerSet3,
                                loserSet3,
                                winnerSet3Sup,
                                loserSet3Sup,
                              );

                              const setCell = (games, oppGames, displaySup, isLoserRow) => {
                                const empty = isLoserRow && result.golfer2 == null;
                                const won = !empty && didWinSet(games, oppGames);
                                let value;
                                if (empty) {
                                  value = "-";
                                } else if (isLoserRow) {
                                  value = games != null ? games : 0;
                                } else {
                                  value = games;
                                }
                                return (
                                  <td className={won ? "strong runs" : ""}>
                                    <span className="position-relative">
                                      {value}
                                      {!empty && displaySup != null ? (
                                        <sup>{displaySup}</sup>
                                      ) : null}
                                    </span>
                                  </td>
                                );
                              };

                              return (
                                <table className="centerAlign linescore">
                                  <tbody>
                                    {/* Winner Row (First) */}
                                    <tr>
                                      <td className="leftAlign strong">
                                        <div className="infodiv_sec">
                                          <div>
                                            <img
                                              className="align-middle"
                                              style={{
                                                width: 40,
                                                borderRadius: "50%",
                                                marginRight: 8,
                                              }}
                                              src={winnerIcon}
                                              alt="Winner"
                                            />
                                          </div>
                                          <div className="d-block w-100 g-textCol">
                                            {winnerName || "Player"} <br />{" "}
                                            <b
                                              className={
                                                winnerIsHome
                                                  ? "homeTextVAl d-block pt-2"
                                                  : "awayTextVAl d-block pt-2"
                                              }
                                            >
                                              {winnerIsHome ? "Home" : "Away"}
                                            </b>
                                          </div>
                                        </div>
                                      </td>
                                      {setCell(winnerSet1, loserSet1, tb1.topSup, false)}
                                      {setCell(winnerSet2, loserSet2, tb2.topSup, false)}
                                      {showThirdSet &&
                                        setCell(winnerSet3, loserSet3, tb3.topSup, false)}
                                    </tr>

                                    {/* Loser Row (Second) - BYE: show "Bye" only, no Opponent/Away/icon */}
                                    <tr>
                                      <td className="leftAlign strong">
                                        <div className="infodiv_sec">
                                          {result.golfer2 != null ? (
                                            <>
                                              <div>
                                                <img
                                                  className="align-middle"
                                                  style={{
                                                    width: 40,
                                                    borderRadius: "50%",
                                                    marginRight: 8,
                                                  }}
                                                  src={loserIcon}
                                                  alt="Loser"
                                                />
                                              </div>
                                              <div className="d-block">
                                                {loserName || "Player"} <br />{" "}
                                                <b
                                                  className={
                                                    !winnerIsHome
                                                      ? "homeTextVAl d-block pt-2"
                                                      : "awayTextVAl d-block pt-2"
                                                  }
                                                >
                                                  {!winnerIsHome
                                                    ? "Home"
                                                    : "Away"}
                                                </b>
                                              </div>
                                            </>
                                          ) : (
                                            <div
                                              className="d-block"
                                              style={{ color: "#666" }}
                                            >
                                              Bye
                                            </div>
                                          )}
                                        </div>
                                      </td>
                                      {setCell(loserSet1, winnerSet1, tb1.bottomSup, true)}
                                      {setCell(loserSet2, winnerSet2, tb2.bottomSup, true)}
                                      {showThirdSet &&
                                        setCell(loserSet3, winnerSet3, tb3.bottomSup, true)}
                                    </tr>
                                  </tbody>
                                </table>
                              );
                            })()}
                        </div>
                        <div className="retiredWin">
                          {resultRows.length > 0 &&
                            (() => {
                              const result = resultRows[0];
                              let statusMessage = null;

                              if (result?.default_winner) {
                                const isCurrentUserWinner =
                                  String(result.default_winner) ===
                                    String(userId) ||
                                  (result.default_winner === result.golfer1 &&
                                    String(result.golfer1) ===
                                      String(userId)) ||
                                  (result.default_winner === result.golfer2 &&
                                    result.golfer2 != null &&
                                    String(result.golfer2) === String(userId));
                                statusMessage = isCurrentUserWinner
                                  ? result.golfer2 == null
                                    ? "Bye"
                                    : "Default Win"
                                  : "Default Loss";
                              } else if (
                                result?.opponentRetired &&
                                result.opponentRetired !== null
                              ) {
                                // Check if current user retired
                                if (
                                  result.opponentRetired === result.golfer1 ||
                                  result.opponentRetired === result.golfer2
                                ) {
                                  const isCurrentUserRetired =
                                    (result.opponentRetired ===
                                      result.golfer1 &&
                                      result.golfer1 === userId) ||
                                    (result.opponentRetired ===
                                      result.golfer2 &&
                                      result.golfer2 === userId);
                                  statusMessage = isCurrentUserRetired
                                    ? "Retired"
                                    : "Opponent Retired";
                                } else {
                                  statusMessage = "Opponent Retired";
                                }
                              } else if (
                                result?.puttoff_winner &&
                                result.puttoff_winner !== null
                              ) {
                                statusMessage = "Bye";
                              } else if (
                                result?.matchStatus === "BYE" ||
                                (result.golfer2 == null && result.golfer1)
                              ) {
                                statusMessage = "BYE";
                              }

                              return statusMessage ? (
                                <p
                                  style={{
                                    color: "#666",
                                    marginTop: "10px",
                                    fontSize: "14px",
                                    marginLeft: "98px",
                                  }}
                                >
                                  {statusMessage}
                                </p>
                              ) : null;
                            })()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="clear" />
          <div className="section">
            <div
              id="view-back"
              className={isPreviewMode ? "view-back-post-instruction" : undefined}
            >
              <div className="buttonwrapper mb-5">
                {isPreviewMode ? (
                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                      justifyContent: "center",
                      alignItems: "center",
                      background: "transparent",
                      padding: 0,
                    }}
                  >
                    <button
                      type="button"
                      className="green_btn"
                      style={{ minWidth: "95px", padding: "8px 14px" }}
                      onClick={handleFinalPost}
                    >
                      Post
                    </button>
                    <button
                      type="button"
                      className="gray_btn"
                      style={{ minWidth: "95px", padding: "8px 14px" }}
                      onClick={() => {
                        const backSearch =
                          previewMeta.returnTo ||
                          `?matchId=${matchId || ""}&userId=${opponentId || scheduleUserId || userId || ""}`;
                        const scores = previewPayload?.scores;
                        const draftFormData = scores
                          ? {
                              bDefault: previewPayload.bDefault || "false",
                              puttoff_winner:
                                previewPayload.puttoff_winner || "",
                              bVerify: previewPayload.bVerify || "false",
                              opponentRetired:
                                previewPayload.opponentRetired || "false",
                              scores: { ...scores },
                            }
                          : null;
                        navigate(
                          {
                            pathname: "/carter/schedule/postresult",
                            search: backSearch.startsWith("?")
                              ? backSearch
                              : `?${backSearch}`,
                          },
                          {
                            state: draftFormData
                              ? {
                                  draftFormData,
                                  draftStartDate: previewPayload.playedDate,
                                }
                              : undefined,
                          },
                        );
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Link
                      to={`/carter/schedule?userId=${scheduleUserId || opponentId || userId || ""}`}
                      className="green_btn"
                      style={{
                        minWidth: "95px",
                        padding: "8px 14px",
                        display: "inline-block",
                        textAlign: "center",
                      }}
                    >
                      Back
                    </Link>
                    {Number(userRole) === 1 && matchId && (
                      <Link
                        to={`/carter/schedule/postresult?matchId=${matchId}&userId=${opponentId || scheduleUserId || userId || ""}&editMode=true&full_name=${encodeURIComponent(playerTwoName || playerOneName || "")}&returnPage=view-result&scheduleUserId=${scheduleUserId || userId || ""}&seasonId=${seasonId || ""}`}
                        className="gray_btn"
                        style={{
                          minWidth: "95px",
                          padding: "8px 14px",
                          display: "inline-block",
                          textAlign: "center",
                        }}
                      >
                        Edit
                      </Link>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          {isPreviewMode && (
  <p className="post-instruction">
    If the above information is correct click “Post.” To make
    corrections, Click “back.”
  </p>
)}
        </>
      )}
    </div>
  );
};

export default ViewResult;
